import { useState, useEffect, useLayoutEffect, useCallback } from 'react';

/* Guided product tour — dims and blurs the page, cuts a spotlight around one
   element at a time and explains it. Next / Back / Skip, or Esc to leave.

   The spotlight is four blurred panels around the target rather than one
   overlay with a CSS "hole": `backdrop-filter` only blurs what sits behind the
   element it's on, so a single element with a transparent cut-out can't blur
   the surround. Four panels can.

   Steps whose element isn't on the page are dropped before the tour starts, so
   the same list works across the brand-dependent dashboards without showing an
   arrow pointing at nothing. */

export type TourStep = {
  /** CSS selector for the element to highlight — use a `data-tour` attribute. */
  selector: string;
  title: string;
  body: string;
  /** Preferred side; falls back automatically when there isn't room. */
  place?: 'top' | 'bottom';
};

type Rect = { top: number; left: number; width: number; height: number };

const PAD = 8;          // breathing room around the highlighted element
const CARD_W = 330;
const GAP = 14;         // space between the spotlight and the card

export default function OnboardingTour({
  steps, onClose, accent = '#1976d2',
}: {
  steps: TourStep[];
  /** `reason: 'no-anchors'` means the tour never ran — don't record it as seen. */
  onClose: (completed: boolean, reason?: 'no-anchors') => void;
  accent?: string;
}) {
  const [live, setLive] = useState<TourStep[]>([]);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  // Drop steps whose target isn't rendered for this user / brand / view.
  //
  // Polled rather than checked once: the dashboard paints its cards after data
  // loads, so a single early query can find nothing, and reporting "no steps"
  // then would let the caller mark the tour as seen without ever showing it.
  useEffect(() => {
    let stop = false;
    let waited = 0;
    const WAIT_MS = 250, GIVE_UP_MS = 8000;

    const look = () => {
      if (stop) return;
      const present = steps.filter(s => document.querySelector(s.selector));
      if (present.length) { setLive(present); return; }
      waited += WAIT_MS;
      if (waited >= GIVE_UP_MS) { onClose(false, 'no-anchors'); return; }
      setTimeout(look, WAIT_MS);
    };
    look();
    return () => { stop = true; };
  }, [steps, onClose]);

  const step = live[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Bring the target into view, then measure once it has settled.
  useLayoutEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    measure();
    const t1 = setTimeout(measure, 260);
    const t2 = setTimeout(measure, 560);   // after smooth-scroll settles
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const next = useCallback(() => {
    setI(prev => {
      if (prev + 1 >= live.length) { onClose(true); return prev; }
      return prev + 1;
    });
  }, [live.length, onClose]);

  const back = useCallback(() => setI(p => Math.max(0, p - 1)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(false); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  if (!step || !rect) return null;

  const vw = window.innerWidth, vh = window.innerHeight;
  const hole = {
    top: Math.max(0, rect.top - PAD),
    left: Math.max(0, rect.left - PAD),
    width: Math.min(vw, rect.width + PAD * 2),
    height: Math.min(vh, rect.height + PAD * 2),
  };
  const holeBottom = hole.top + hole.height;
  const holeRight  = hole.left + hole.width;

  // Place the card below the spotlight when there's room, otherwise above it.
  const roomBelow = vh - holeBottom;
  const below = step.place === 'top' ? false : roomBelow > 210;
  const cardTop = below ? holeBottom + GAP : Math.max(GAP, hole.top - 200 - GAP);
  const cardLeft = Math.min(Math.max(GAP, rect.left), vw - CARD_W - GAP);

  const panel: React.CSSProperties = {
    position: 'fixed',
    background: 'rgba(17,24,39,.45)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    zIndex: 10000,
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Blurred surround — four panels leaving the target crisp and readable. */}
      <div style={{ ...panel, top: 0, left: 0, width: '100vw', height: hole.top }} onClick={() => onClose(false)} />
      <div style={{ ...panel, top: holeBottom, left: 0, width: '100vw', height: Math.max(0, vh - holeBottom) }} onClick={() => onClose(false)} />
      <div style={{ ...panel, top: hole.top, left: 0, width: hole.left, height: hole.height }} onClick={() => onClose(false)} />
      <div style={{ ...panel, top: hole.top, left: holeRight, width: Math.max(0, vw - holeRight), height: hole.height }} onClick={() => onClose(false)} />

      {/* Highlight ring (pointer-events off so it never eats a click) */}
      <div
        style={{
          position: 'fixed', top: hole.top, left: hole.left, width: hole.width, height: hole.height,
          border: `2px solid ${accent}`, borderRadius: 14, boxShadow: `0 0 0 4px ${accent}33`,
          pointerEvents: 'none', zIndex: 10001, transition: 'all .25s cubic-bezier(.2,.7,.3,1)',
        }}
      />

      {/* Explanation card */}
      <div
        style={{
          position: 'fixed', top: cardTop, left: cardLeft, width: CARD_W, zIndex: 10002,
          background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px -20px rgba(17,24,39,.45)',
          padding: '18px 20px 16px', transition: 'top .25s cubic-bezier(.2,.7,.3,1), left .25s cubic-bezier(.2,.7,.3,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ background: accent, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 999 }}>
            {i + 1} / {live.length}
          </span>
          <button
            onClick={() => onClose(false)}
            style={{ marginInlineStart: 'auto', background: 'none', border: 'none', color: '#6B7280', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
          >
            Skip tour
          </button>
        </div>

        <h3 style={{ fontSize: 16.5, fontWeight: 800, color: '#111827', marginBottom: 6, lineHeight: 1.3 }}>{step.title}</h3>
        <p style={{ fontSize: 13.5, color: '#42505F', lineHeight: 1.6, marginBottom: 14 }}>{step.body}</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {i > 0 && (
            <button
              onClick={back}
              style={{ background: '#fff', border: '1.5px solid #E5E7EB', color: '#374151', fontWeight: 700, fontSize: 13.5, padding: '9px 16px', borderRadius: 10, cursor: 'pointer' }}
            >
              Back
            </button>
          )}
          <button
            onClick={next}
            style={{ marginInlineStart: 'auto', background: accent, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13.5, padding: '9px 20px', borderRadius: 10, cursor: 'pointer' }}
          >
            {i + 1 >= live.length ? 'Got it' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}
